import React, { useMemo } from 'react';

import selectFiles from '@chenyuwang/select-files';
import { Button, ButtonProps } from '@components/Button';
import type { InputCommonProps } from '@components/NextForms/common';
import { ErrorMessages } from '@components/NextForms/ErrorMessages';
import { FileArrayDisplay } from '@components/utilities/BookingInquiry/FileArrayDisplay';
import type { LocalFile } from '@models/next/FileType';
import { MobxZodArrayField, getDecodeResult } from '@monoid-dev/mobx-zod-form';
import { formStrings } from '@utils/forms';
import { optionalToUndefined } from '@utils/types';
import { observer } from 'mobx-react';
import type { ZodArray } from 'zod';

export interface FileArrayInputProps extends InputCommonProps {
  field: MobxZodArrayField<ZodArray<typeof LocalFile>>;
  accept?: string;
  buttonProps?: Partial<ButtonProps>;
}

export const FileArrayInput: React.FC<FileArrayInputProps> = observer((props) => {
  const {
    field,
    readOnly = false,
    accept = field.type.element.description,
    buttonProps,
    withErrorMessages = true,
  } = props;

  const files = useMemo(() => getDecodeResult(field.decodeResult) ?? [], [field.decodeResult]);

  const displayFiles = useMemo(() => files.map(optionalToUndefined), [files]);

  return (
    <>
      {files.length > 0 ? (
        <FileArrayDisplay
          files={displayFiles}
          onDelete={(i) => {
            field.splice(i, 1, []);
            field.setTouched(true);
          }}
          readOnly={readOnly}
        />
      ) : null}

      <Button
        text={formStrings.upload}
        color="primary"
        size="small"
        disabled={readOnly}
        type="button"
        {...buttonProps}
        onClick={async (e) => {
          buttonProps?.onClick?.(e);

          const uploadFiles = await selectFiles({
            accept,
            multiple: true,
          });

          if (uploadFiles?.length) {
            field.setTouched(true);
            field.push(...Array.from(uploadFiles).map((file) => ({ file })));
          }
        }}
      />

      {withErrorMessages && <ErrorMessages field={field} />}
    </>
  );
});
